import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { locationsApi } from 'api/locations'
import {
  ServiceChargeGroupLocation,
  ServiceChargeGroupsMeta,
  useGetServiceChargeGroupLocationsQuery,
} from 'api/serviceCharges'
import PageHeader from 'components/PageHeader'
import TablePaginationFooter, {
  DEFAULT_PAGE_SIZE,
  PageSize,
} from 'components/TablePaginationFooter'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect, useMemo, useState } from 'react'
import { Container } from 'react-bootstrap-five'
import Helmet from 'react-helmet'
import type { RouteComponentProps } from 'react-router-dom'
import { formatServiceChargeAmount } from '../serviceChargeFormat'
import {
  tableBodyCellClass,
  tableHeaderCellClass,
  tableRowHoverClass,
  tableShellClass,
} from '../tableStyles'

const t = buildTranslate(
  'locations.location_list.service_charge_groups.drill_down'
)

export type Params = { providerUid: string }

// Optionally handed over via router state when navigating from the group
// list's View link (mirrors FeedbackTable's link-state ride-along pattern):
// the group's display name for the page title, so a fresh direct link/
// refresh degrades to the generic title instead of throwing.
export type GroupLocationsLinkState = { groupName?: string } | undefined

const GroupLocationsDrillDown = ({
  match,
  location,
}: RouteComponentProps<Params>) => {
  const [css] = useStyletron()
  const providerUid = decodeURIComponent(match.params.providerUid)
  const groupName = (location.state as GroupLocationsLinkState)?.groupName

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE)

  // A single Route matches every providerUid, so React Router reuses this
  // mounted instance across groups rather than remounting it — reset back
  // to the first page whenever the drilled-into group changes.
  useEffect(() => {
    setPage(1)
    setPageSize(DEFAULT_PAGE_SIZE)
  }, [providerUid])

  // currentData (not data): keyed to the current args, so on navigation to a
  // different providerUid (no remount) it goes undefined while the new fetch
  // runs, instead of `data` sticking with the OLD group's rows under the NEW
  // title. Mirrors RecoveryAI/CaseDetail/index.tsx's same RTK Query rationale.
  const { currentData, isFetching, isUninitialized, isError } =
    useGetServiceChargeGroupLocationsQuery({
      providerUid,
      page,
      perPage: pageSize,
    })

  // page/perPage are also part of the query's args, so currentData goes
  // undefined on a same-group page turn too, not just a group switch. This
  // holds the last rows/meta seen FOR THIS providerUid so a page turn keeps
  // the table mounted (showing the stale page) instead of blanking to the
  // loading state — reset whenever providerUid itself changes, so a real
  // group switch still avoids the stale-group flash.
  const [displayed, setDisplayed] = useState<{
    providerUid: string
    data: ServiceChargeGroupLocation[]
    meta: ServiceChargeGroupsMeta
  } | null>(null)
  useEffect(() => {
    if (currentData) setDisplayed({ providerUid, ...currentData })
  }, [currentData, providerUid])

  const isSameGroupRefetch =
    isFetching && displayed !== null && displayed.providerUid === providerUid
  const rows = isSameGroupRefetch ? displayed.data : currentData?.data ?? []

  const { data: locationsData } = locationsApi.useGetLocationsQuery({})
  const locationNameById = useMemo(
    () => new Map((locationsData?.locations ?? []).map(l => [l.id, l.name])),
    [locationsData]
  )

  const totalCount = isSameGroupRefetch
    ? displayed.meta.total_count
    : currentData?.meta.total_count ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  const handlePageSizeChange = (size: PageSize) => {
    setPageSize(size)
    setPage(1)
  }

  const title = groupName
    ? (t('title', { name: groupName }) as string)
    : (t('title_generic') as string)
  const pageTitle = groupName
    ? (t('page_title', { name: groupName }) as string)
    : (t('page_title_generic') as string)

  const renderBody = (): React.ReactNode => {
    if (isUninitialized || (isFetching && !isSameGroupRefetch)) {
      return (
        <p
          role="status"
          className={`body-text-3 ${css({ color: palette.grey70 })}`}
        >
          {t('loading')}
        </p>
      )
    }
    if (isError) {
      return (
        <div data-testid="group-locations-fetch-error" role="alert">
          <Notification kind="negative" size="full">
            <Text.BodyText4 color="grey70" className="p-xs" tag="div">
              {t('fetch_error')}
            </Text.BodyText4>
          </Notification>
        </div>
      )
    }
    if (rows.length === 0) {
      return (
        <div role="status">
          <Text.BodyText3
            color="grey70"
            className="py-xl text-center"
            tag="div"
          >
            {t('empty')}
          </Text.BodyText3>
        </div>
      )
    }
    return (
      <>
        <table
          className={`full-width ${tableShellClass(css)}`}
          role="table"
          data-testid="group-locations-table"
        >
          <thead>
            <tr>
              <th scope="col" className={tableHeaderCellClass(css)}>
                {t('table.location')}
              </th>
              <th scope="col" className={tableHeaderCellClass(css)}>
                {t('table.amount')}
              </th>
              <th scope="col" className={tableHeaderCellClass(css)}>
                {t('table.enabled')}
              </th>
              <th scope="col" className={tableHeaderCellClass(css)}>
                {t('table.delivery_override')}
              </th>
              <th scope="col" className={tableHeaderCellClass(css)}>
                {t('table.takeout_override')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const isLastRow = index === rows.length - 1
              const cellClassName = tableBodyCellClass(css, isLastRow)
              // OPEN already renders the full "(amount varies)" phrase —
              // matches the Detail page, which never prefixes OPEN with a
              // type label either.
              const amountText =
                row.amount_type === 'OPEN'
                  ? formatServiceChargeAmount(row)
                  : `${t(
                      `amount_type_labels.${row.amount_type}`
                    )} ${formatServiceChargeAmount(row)}`
              return (
                <tr key={row.location_id} className={tableRowHoverClass(css)}>
                  <td className={cellClassName}>
                    <Text.BodyText4 bold color="grey90">
                      {locationNameById.get(row.location_id) ?? row.location_id}
                    </Text.BodyText4>
                  </td>
                  <td className={cellClassName}>{amountText}</td>
                  <td className={cellClassName}>
                    {row.thanx_enabled ? t('yes') : t('no')}
                  </td>
                  <td className={cellClassName}>
                    {row.criteria_override.handoffs.includes('DELIVERY')
                      ? t('yes')
                      : t('not_overridden')}
                  </td>
                  <td className={cellClassName}>
                    {row.criteria_override.handoffs.includes('PICKUP')
                      ? t('yes')
                      : t('not_overridden')}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {isSameGroupRefetch && (
          <span className="sr-only" role="status" aria-live="polite">
            {t('loading')}
          </span>
        )}
        <TablePaginationFooter
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </>
    )
  }

  return (
    <PageHeader
      title={title}
      description={t('description')}
      backPath="/service_charges"
    >
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Container>{renderBody()}</Container>
    </PageHeader>
  )
}

export default GroupLocationsDrillDown
